import { EventTypeEnum, ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';

import { type CloudError } from 'repository/CloudError';
import { type CloudResult } from 'repository/repository';
import { type EventCost } from 'typedefs/event';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { getDoublesName, getFullDoublesName, getParticipantsDisplayName, getTeamPlayerNames } from 'utils/participant';

interface GetPromoteParticipantsDataProps {
  addToTeamParticipants: ParticipantInterface[];
  eventType: EventTypeEnum;
  promoteParticipants: ParticipantInterface[];
}

export interface PromoteParticipantData {
  doublesNames?: { id: string; name: string }[] | [];
  individualParticipantIds?: string[];
  individualParticipants?: ParticipantInterface[];
  isDoubles?: boolean;
  isTeam?: boolean;
  name: string;
  participantId: string;
  personIds?: string[];
  promoteDoublesName?: string;
  teamPlayerNames?: { id: string; name: string }[];
}

export interface EventCostResult {
  eventCostData?: EventCost[];
  costLoading?: boolean;
  error?: CloudError;
}

export const calculateToPay = (eventFees: number, paid: number): number => {
  const toPay = Number(((eventFees - paid) / 100).toFixed(2));
  return toPay >= 0 ? toPay : 0;
};
export const playerName = (player) =>
  [player.person?.standardFamilyName?.toUpperCase() ?? '', player.person?.standardGivenName ?? ''].join(', ');

export const getPromoteParticipantsData = ({
  addToTeamParticipants,
  eventType,
  promoteParticipants,
}: GetPromoteParticipantsDataProps): PromoteParticipantData[] =>
  (!promoteParticipants?.includes(undefined) &&
    promoteParticipants
      ?.filter((p) => !!p)
      .map((participant) => {
        const isTeam = participant.participantType === ParticipantTypeEnum.TEAM;
        const isDoubles = eventType === EventTypeEnum.DOUBLES;
        const isAddToTeam = !!addToTeamParticipants;
        const doublesNames = getFullDoublesName(participant);
        const teamPlayerNames = getTeamPlayerNames(
          isAddToTeam ? { individualParticipants: addToTeamParticipants } : participant,
          isTeam,
        );

        return {
          doublesNames,
          isDoubles: isDoubles,
          isTeam: isTeam,
          name: getParticipantsDisplayName(participant),
          participantId: participant.participantId,
          teamPlayerNames,
          ...(isAddToTeam || (!isTeam && !isDoubles)
            ? {
                individualParticipantIds: addToTeamParticipants?.map((p: ParticipantInterface) => p.participantId) || [
                  participant?.participantId,
                ],
                individualParticipants: addToTeamParticipants || [participant],
                personIds: addToTeamParticipants?.map((p) => p.personId) || [participant?.personId],
              }
            : (isDoubles || isTeam) && {
                individualParticipantIds: participant.individualParticipantIds,
                individualParticipants: participant.individualParticipants,
                personIds: participant?.individualParticipants?.map((p) => p?.personId),
                promoteDoublesName: eventType === EventTypeEnum.TEAM ? '' : getDoublesName(participant),
              }),
        };
      })) ||
  [];

export const getIsTeam = (promoteParticipantsData: PromoteParticipantData[]): boolean =>
  !!promoteParticipantsData?.length && promoteParticipantsData?.every((p) => p?.isTeam);

export const handleSetEventCostResult = async (
  participantEventCostData: EventCost[],
  getEventCostResult: () => Promise<CloudResult<EventCost[]>>,
  setEventCostResult: (eventCostResult: EventCostResult) => void,
  participantIds: string[],
) => {
  if (participantEventCostData) {
    setEventCostResult({ eventCostData: participantEventCostData });
    return;
  }
  if (participantIds?.length && !participantIds?.includes(undefined)) {
    setEventCostResult({ costLoading: true });
    const eventCostResult = await getEventCostResult();
    if (eventCostResult?.error) {
      setEventCostResult({ error: eventCostResult.error, costLoading: false });
    } else {
      setEventCostResult({ eventCostData: eventCostResult.data, costLoading: false });
    }
  }
};
